import { CustomThemedResource } from "@/comps/image"
import { assetUrl } from "@/utils/cdn"
import { useCoins } from "@/utils/coins"
import prettyMillions from "@/utils/millions"
import clsx from "clsx"
import { PageName } from "controllers/dashboard"
import {
	AITool,
	DynamicEditorProps,
} from "controllers/editors"
import { useTranslation } from "next-i18next"
import { useEffect, useMemo, useState } from "react"
import {
	discoverIcon,
	homeIcon,
	projectsIcon,
	videoGptIcon,
} from "./icons"
import {
	SessionsSidebar,
	SessionsState,
} from "./sessions-sidebar"

type ItemChild = {
	name: string
	icon?: JSX.Element
	href?: string
	action?: () => void
	content?: {
		icon?: JSX.Element
		name: string
		href: string
		action?: () => void
	}[]
}

type ItemType = {
	name: "home" | "discover" | "videoGpt"
	href: string
	title: string
	icon: JSX.Element
	childs: ItemChild[] | null
}

interface SideBarProps {
	activePage: PageName
	isClosed: boolean
	sessionsGroup: SessionsState | null
	activeChild: string | null
	selectedTool: AITool | null
	changeActiveChild: (name: string | null) => void
	changeSelectedPage: (name: PageName) => Promise<void>
	closeSidebar: () => void
	setSubscriptionPopupOpen: (v: boolean) => void
	selectCreatorTool: (
		tool: AITool,
		content: DynamicEditorProps,
		changePage?: boolean,
	) => Promise<void>
}

export function SideBar(props: SideBarProps) {
	const {
		activePage: page,
		isClosed,
		activeChild,
		selectedTool,
		changeActiveChild,
		closeSidebar,
		setSubscriptionPopupOpen,
		changeSelectedPage,
		selectCreatorTool,
	} = props

	const [renderChilds, setRenderChilds] = useState(
		page !== "home",
	)

	const [showSessions, setShowSessions] = useState(false)

	const items: ItemType[] = useMemo(
		() => [
			{
				name: "home",
				href: "/home",
				title: "lbl_home",
				icon: homeIcon("22", "22"),
				childs: null,
			},
			{
				name: "videoGpt",
				href: "/video-gpt",
				title: "txt_video_gpt",
				icon: videoGptIcon("22", "22"),
				childs: [
					{
						name: "lbl_projects",
						href: "/chat",
						icon: projectsIcon("22", "22"),
						action: () => changeSelectedPage("chat"),
					},
				],
			},
			{
				name: "discover",
				href: "/discover",
				title: "lbl_discover",
				icon: discoverIcon("22", "22"),
				childs: null,
			},
		],
		[changeSelectedPage],
	)

	const initialIndex = items.findIndex(
		(a) => a.name === page,
	)
	const [index, setIndex] = useState(
		initialIndex !== -1 ? initialIndex : 0,
	)

	useEffect(() => {
		if (props.sessionsGroup?.selectedSessionId !== null) {
			setIndex(1)
			setRenderChilds(true)
		}

		const newIndex = items.findIndex((a) => a.name === page)
		if (page !== "chat") {
			setIndex(newIndex !== -1 ? newIndex : 0)
			setRenderChilds(
				newIndex !== -1 && items[newIndex].childs
					? true
					: false,
			)
		} else {
			setIndex(1)
			setShowSessions(true)
			setRenderChilds(true)
		}
	}, [items, page, props.sessionsGroup?.selectedSessionId])

	const item = items[index]

	return (
		<>
			<div
				id="dashboard-sidebar-bg"
				onClick={(e) => {
					if (e.currentTarget instanceof HTMLElement) {
						if (
							e.currentTarget.id === "dashboard-sidebar-bg"
						) {
							closeSidebar()
						}
					}
				}}
				className={clsx(
					isClosed
						? " pointer-events-none opacity-0"
						: "opacity-100",
					"fixed left-0 top-0 z-[99] h-full w-full bg-color-popup transition-opacity desktop:hidden",
				)}
			/>
			<div
				className={clsx(
					"fixed left-0 top-0 z-[100] h-[100svh] w-[80%] overflow-hidden bg-color-sidebar-bg transition-all tablet:w-[240px]",
					isClosed
						? "-translate-x-full tablet:translate-x-[-240px]"
						: "translate-x-0",
				)}>
				<div
					className={clsx(
						"flex h-full shrink-0 flex-row pb-[70px] pt-[100px] transition-all",
						renderChilds &&
							"-translate-x-full tablet:-translate-x-[240px]",
					)}>
					<div
						className={clsx(
							"h-full w-full shrink-0 flex-col px-[12px] tablet:flex",
							"ease-in-out tablet:gap-[44px]",
							"group items-start bg-color-sidebar-bg",
						)}>
						<div className="flex h-full w-full flex-col justify-between">
							<div
								className={clsx(
									"flex w-full flex-col gap-2 tablet:items-center",
								)}>
								{items.map((item, index) => {
									return (
										<SideBarItem
											key={index}
											item={item}
											isClosed={isClosed}
											isSelected={page === item.name}
											changeSelectedPage={() => {
												changeSelectedPage(item.name)
												if (item.childs !== null) {
													setRenderChilds(true)
													setIndex(index)
												}
												if (item.name === "discover") {
													changeActiveChild(
														"lbl_ad_examples",
													)
												}
												if (typeof window !== undefined) {
													if (window.innerWidth < 1224) {
														closeSidebar()
													}
												}
											}}
										/>
									)
								})}
							</div>
						</div>
					</div>
					<div
						className={clsx(
							"flex h-full w-full flex-col",
							"no-scrollbar shrink-0 overflow-y-auto ease-in-out",
							"items-start bg-color-sidebar-bg",
							isClosed && "hidden",
							item.name !== "videoGpt" && "px-3",
						)}>
						<div
							className={clsx(
								"w-full",
								item.name === "videoGpt" && "px-3",
							)}>
							<button
								onClick={() => {
									changeSelectedPage("home")
									setRenderChilds(false)
									setShowSessions(false)
									setIndex(0)
								}}
								className={clsx(
									"mb-3 flex h-[40px] w-full shrink-0 flex-row items-center",
									"justify-start gap-2 rounded-[4px] px-2 hover:bg-blue-100",
								)}>
								<div className="rotate-90 [--icon-color:var(--color-blue-600)]">
									{ArrowIcon()}
								</div>
								<p className="text-[14px] font-400 tracking-[0.3px] text-blue-600">
									Back to Home
								</p>
							</button>
						</div>
						{item.childs && item.name !== "videoGpt" && (
							<div className="flex w-full flex-col gap-1">
								{item.childs.map((child, index) => {
									return (
										<SideBarItemChild
											key={index}
											{...child}
											activeChild={activeChild}
											changeActiveChild={changeActiveChild}
										/>
									)
								})}
							</div>
						)}

						{item.childs && item.name === "videoGpt" && (
							<div
								className={clsx(
									"flex max-h-[calc(100%-55px)] w-full flex-row transition-all duration-200",
									showSessions
										? "-translate-x-full"
										: "translate-x-0",
								)}>
								<div className="flex w-full shrink-0 flex-col gap-1 px-3">
									{item.childs.map((child, index) => {
										return (
											<ProjectsMenu
												key={index}
												{...child}
												activeChild={activeChild}
												changeActiveChild={
													changeActiveChild
												}
												setShowSessions={setShowSessions}
											/>
										)
									})}
								</div>

								<div className="flex max-h-full w-full shrink-0 px-3">
									{props.sessionsGroup !== null &&
										props.activePage === "chat" && (
											<SessionsSidebar
												{...props.sessionsGroup}
												setSubscriptionPopupOpen={
													setSubscriptionPopupOpen
												}
												onClick={() => {
													if (typeof window !== undefined) {
														if (window.innerWidth < 1224) {
															closeSidebar()
														}
													}
												}}
											/>
										)}
								</div>
							</div>
						)}

						{/* {item.name === "chat" &&
							props.sessionsGroup !== null && (
								<SessionsSidebar
									{...props.sessionsGroup}
									setSubscriptionPopupOpen={
										setSubscriptionPopupOpen
									}
									onClick={() => {
										if (typeof window !== undefined) {
											if (window.innerWidth < 1224) {
												closeSidebar()
											}
										}
									}}
								/>
							)} */}
					</div>
				</div>

				<div
					className={clsx(
						"fixed left-3 top-3 flex w-full items-center px-1 desktop:top-[6px]",
					)}>
					<button
						onClick={() => closeSidebar()}
						className="[--icon-color:var(--color-blue-800)] desktop:hidden">
						{CloseIcon()}
					</button>

					<a
						href="/home"
						className="max-w-[90px] desktop:h-[35px] desktop:w-[101px] desktop:max-w-none">
						<CustomThemedResource
							source="/comps/logo"
							format="svg"
							alt="Zoomerang main logo"
						/>
					</a>
				</div>

				<div
					className={clsx(
						"fixed bottom-3 left-0 w-full",
						isClosed ? "px-2" : "px-3",
					)}>
					<TokensAndStorage
						setSubscriptionPopupOpen={
							setSubscriptionPopupOpen
						}
					/>
				</div>
			</div>
		</>
	)
}

interface SideBarItemChildProps {
	name: string
	icon?: JSX.Element
	href?: string
	action?: () => void
	content?: {
		icon?: JSX.Element
		name: string
		href: string
		action?: () => void
	}[]
	activeChild: string | null
	changeActiveChild: (name: string | null) => void
}

function SideBarItemChild(props: SideBarItemChildProps) {
	const {
		name,
		icon,
		href,
		action,
		content,
		activeChild,
		changeActiveChild,
	} = props
	const { t } = useTranslation()
	const [isOpen, setIsOpen] = useState(true)

	return (
		<>
			{href ? (
				<a
					key={name}
					href={href}
					onClick={(e) => {
						if (action) {
							e.preventDefault()
							action()
						}
					}}
					className={clsx(
						"flex h-11 w-full shrink-0 flex-row items-center gap-2 rounded-[4px] px-2 hover:bg-blue-100",
						activeChild === name
							? "bg-blue-100 text-blue-900"
							: "text-blue-600",
						!content && "my-[2px]",
					)}>
					{icon && (
						<div
							className={clsx(
								"flex items-center justify-center",
								"transition-all",
								activeChild === name
									? "[--icon-color:var(--color-blue-900)]"
									: "[--icon-color:var(--color-blue-600)]",
							)}>
							{icon}
						</div>
					)}
					<p
						className={clsx(
							"text-[14px] font-500 tracking-[0.3px]",
						)}>
						{t(name)}
					</p>
				</a>
			) : (
				<button
					onClick={() => setIsOpen((o) => !o)}
					className={clsx(
						"flex h-8 w-full shrink-0 flex-row items-center gap-2 rounded-[4px] px-2",
						"hover:bg-blue-100",
					)}>
					{content && (
						<div
							className={clsx(
								"transition-all",
								isOpen
									? "[--icon-color:var(--color-blue-500)]"
									: "-rotate-90 [--icon-color:var(--color-blue-700)]",
							)}>
							{ArrowIcon()}
						</div>
					)}
					{icon && (
						<div className="[--icon-color:var(--color-blue-800)]">
							{icon}
						</div>
					)}
					<p
						className={clsx(
							" tracking-[0.3px] transition-all",
							isOpen
								? "text-[14px] font-500 text-blue-500"
								: "text-[14px] font-500 text-blue-700",
						)}>
						{t(name)}
					</p>
				</button>
			)}

			{content && (
				<div
					style={{
						height: isOpen
							? `${
									(content?.length ?? 0) * 44 +
									((content?.length ?? 1) - 1) * 8
							  }px`
							: "0px",
					}}
					className="flex w-full shrink-0 flex-col gap-[8px] overflow-hidden transition-all">
					{content?.map((a) => (
						<a
							key={a.name}
							href={a.href}
							onClick={(e) => {
								changeActiveChild(a.name)
								if (a.action) {
									e.preventDefault()
									a.action()
								}
							}}
							className={clsx(
								"flex h-11 w-full shrink-0 flex-row items-center gap-2",
								"rounded-[4px] px-2 text-blue-600 hover:bg-blue-100",
								activeChild === a.name
									? "bg-blue-100 text-blue-900"
									: "text-blue-600",
							)}>
							{a.icon && (
								<div
									className={clsx(
										"flex items-center justify-center",
										"transition-all [--icon-color:var(--color-blue-600)]",
										activeChild === a.name
											? "[--icon-color:var(--color-blue-900)]"
											: "[--icon-color:var(--color-blue-600)]",
									)}>
									{a.icon}
								</div>
							)}
							<p
								className={clsx(
									"text-[14px] font-500 tracking-[0.3px]",
								)}>
								{t(a.name)}
							</p>
						</a>
					))}
				</div>
			)}
		</>
	)
}

function ProjectsMenu(
	props: SideBarItemChildProps & {
		setShowSessions: (v: boolean) => void
	},
) {
	const { name, icon, href, action, setShowSessions } =
		props
	const { t } = useTranslation()

	return (
		<a
			key={name}
			href={href}
			onClick={(e) => {
				if (action) {
					e.preventDefault()
					action()
					setShowSessions(true)
				}
			}}
			className={clsx(
				"hover:bg-blue-100",
				"relative w-full gap-[8px]",
				"flex h-11 items-center justify-between rounded-[4px] px-[8px] transition-all",
			)}>
			<div className="flex flex-row items-center gap-2">
				<div
					className={clsx(
						"[--icon-color:var(--color-blue-600)]",
						"flex items-center justify-center",
					)}>
					{icon}
				</div>
				<span
					className={clsx(
						"text-blue-600",
						"text-[14px] font-500 tracking-[0.4px]",
					)}>
					{t(name)}
				</span>
			</div>

			<div className="-rotate-90 [--icon-color:var(--color-blue-600)]">
				{ArrowIcon()}
			</div>
		</a>
	)
}

export function SideBarItem(props: {
	item: ItemType
	isSelected: boolean
	isClosed: boolean
	changeSelectedPage: () => void
}) {
	const { item, changeSelectedPage, isSelected } = props

	const { t } = useTranslation()

	return (
		<div className="relative w-full">
			<a
				href={item.href}
				onClick={(e) => {
					e.preventDefault()
					changeSelectedPage()
				}}
				className={clsx(
					isSelected ? "bg-blue-100" : "hover:bg-blue-100",
					"relative w-full gap-[8px]",
					"flex h-11 items-center justify-between rounded-[4px] px-[8px] transition-all",
				)}>
				<div className="flex flex-row items-center gap-2">
					<div
						className={clsx(
							isSelected
								? "[--icon-color:var(--color-blue-900)]"
								: "[--icon-color:var(--color-blue-600)]",
							"flex items-center justify-center",
						)}>
						{item.icon}
					</div>
					<span
						className={clsx(
							isSelected
								? "text-blue-900"
								: "text-blue-600",
							"text-[14px] font-500 tracking-[0.4px]",
						)}>
						{t(item.title)}
					</span>
				</div>
				{(item.childs || item.href === "/chat") && (
					<div className="-rotate-90 [--icon-color:var(--color-blue-600)]">
						{ArrowIcon()}
					</div>
				)}
			</a>
		</div>
	)
}

function TokensAndStorage(props: {
	setSubscriptionPopupOpen: (v: boolean) => void
}) {
	const { setSubscriptionPopupOpen } = props
	const { t } = useTranslation()

	const wallet = useCoins()
	const smallTokensTooltip =
		wallet?.renewInfo === null ||
		wallet?.renewInfo.cancelled === true

	const tokensSummery =
		(wallet?.permanentTokens ?? 0) +
		(wallet?.temporaryTokens ?? 0)

	return (
		<div
			className={clsx(
				"desktop:rounded-[4px]",
				"w-full self-center rounded-[4px] bg-blue-100",
			)}>
			<button
				onClick={() => setSubscriptionPopupOpen(true)}
				className={clsx(
					"desktop:justify-between desktop:px-[8px] desktop:py-[12px]",
					"flex w-full items-center justify-between px-2 py-3",
				)}>
				<div className="flex items-center gap-[2px]">
					<span
						className={clsx(
							"tracking-[0.6px] desktop:block",
							"text-[15px] font-400 text-blue-800",
						)}>
						{t("lbl_tokens")}
					</span>
					<div className="relative z-10">
						<img
							src={assetUrl("/general/info.svg")}
							alt="info icon"
							className={clsx(
								"peer h-[16px] w-[16px] cursor-pointer",
							)}
						/>
						<div
							className={clsx(
								smallTokensTooltip
									? "-top-[70px]"
									: "-top-[80px]",
								"absolute left-1/4 -translate-x-1/4",
								"z-20 hidden peer-hover:flex",
							)}>
							<div
								className={clsx(
									"relative rounded-[8px] bg-color-tooltip px-[12px]",
									"py-[8px]",
								)}>
								<p
									className={clsx(
										smallTokensTooltip
											? "line-clamp-2 min-w-[180px]"
											: "line-clamp-3 min-w-[190px]",
										"tracking-[0.24px] text-blue-100",
										"text-[12px] font-400 leading-normal",
									)}>
									{wallet?.renewInfo
										? t("fs_tokens_renew", {
												tokens: tokensSummery,
												renew: wallet.renewInfo.tokens,
										  })
										: tokensSummery > 0
										? t("fs_tokens_available", {
												tokens: tokensSummery,
										  })
										: t("txt_dont_have_tokens")}
								</p>
								<div
									className={clsx(
										"h-[10px] w-[10px] bg-color-tooltip",
										"absolute left-1/4 rotate-45 rounded-[2px]",
										"bottom-[-5px] -translate-x-1/4",
									)}
								/>
							</div>
						</div>
					</div>
				</div>

				<div className="flex items-center">
					<img
						src={assetUrl("/general/token.svg")}
						alt="token icon"
						className="h-[16px] w-[16px]"
					/>
					<span
						className={clsx(
							"text-[15px] font-500 text-blue-700 desktop:text-[15px]",
						)}>
						{wallet ? prettyMillions(tokensSummery) : "-"}
					</span>
				</div>
			</button>
		</div>
	)
}

const CloseIcon = () => {
	return (
		<svg
			width="28"
			height="28"
			viewBox="0 0 32 32"
			fill="none"
			xmlns="http://www.w3.org/2000/svg">
			<path
				fillRule="evenodd"
				clipRule="evenodd"
				d="M8.53493 6.6498C8.01423 6.1291 7.17001 6.1291 6.64931 6.6498C6.12861 7.1705 6.12861 8.01472 6.64931 8.53542L14.114 16.0001L6.64931 23.4648C6.12861 23.9855 6.12861 24.8297 6.64931 25.3504C7.17001 25.8711 8.01423 25.8711 8.53493 25.3504L15.9996 17.8857L23.4643 25.3504C23.985 25.8711 24.8292 25.8711 25.3499 25.3504C25.8706 24.8297 25.8706 23.9855 25.3499 23.4648L17.8852 16.0001L25.3499 8.53542C25.8706 8.01472 25.8706 7.1705 25.3499 6.6498C24.8292 6.1291 23.985 6.1291 23.4643 6.6498L15.9996 14.1145L8.53493 6.6498Z"
				fill="var(--icon-color)"
			/>
		</svg>
	)
}

const ArrowIcon = () => {
	return (
		<svg
			width="20"
			height="20"
			viewBox="0 0 20 20"
			fill="none"
			xmlns="http://www.w3.org/2000/svg">
			<path
				d="M5.38086 7.69043L10 12.3096L14.6191 7.69043"
				stroke="var(--icon-color)"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	)
}
